import { ModelTopologyFeatureBinding } from '../binding/ModelTopologyFeatureBinding';
import type {Viewer} from '../../viewer';
import type {Subscription} from 'rxjs';
import type {FeatureExecutionRuntime} from '../execution/FeatureExecutionRuntime';
import {FeatureDependencyGraph} from '../graph/FeatureDependencyGraph';
import {RevolveAxisResolver} from '../edit/revolve/RevolveAxisResolver';
import {
    HISTORY_SKETCH_RUNTIME_RESTORE_COMMIT_REASON,
    SKETCH_SCALE_ENTITIES_COMMIT_REASON,
} from '../../sketch/SketchDefinitionCommitReasons';
import {isStaleWorkerResponseError} from '../../worker/StaleWorkerResponseError';
import type {SketchDefinitionCommittedEvent} from '../../mesh/sketch/SketchGroup';
import {FeatureTimelineRebuildExecutor} from './FeatureTimelineRebuildExecutor';
import {
    REFERENCE_TARGET_REBUILT_COMMIT_REASON,
} from '../../reference/sketch-entity-reference/model/SketchEntityReferencePropagation';
import type {
    FeatureRebuildResult,
    QueuedSketchRebuildRequest,
    SketchPrefixEditRebuildInput,
} from '../../../types/feature';

export type {FeatureRebuildResult} from '../../../types/feature';

/**
 * 命令重写结果。
 */
interface RewriteCommandResult {
    /** 重写后的命令。 */
    command: CadCommand;
    /** 是否发生变化。 */
    changed: boolean;
}

/**
 * 编辑草图特征预览刷新结果。
 */
interface EditSketchPreviewRefreshCommitResult {
    /** 当前 committed 边界是否已经由编辑草图预览链路接管。 */
    handled: boolean;
    /** 是否允许清理本次 committed revision 对应的 dirty 记录。 */
    shouldClearDirtyRevision: boolean;
}

/**
 * 特征重建调度器。
 */
export class FeatureRebuildOrchestrator {
    /** 当前 Viewer 实例。 */
    private readonly viewer: Viewer;
    /** 特征执行运行时。 */
    private readonly runtime: FeatureExecutionRuntime;
    /** 特征依赖图。 */
    private readonly dependencyGraph: FeatureDependencyGraph;
    /** 测试或宿主显式注入的阶段 B 执行器；默认按本次冻结输入创建。 */
    private readonly timelineExecutor?: FeatureTimelineRebuildExecutor;
    /** 草图 dirty revision 缓存。 */
    private readonly dirtySketchRevisionMap = new Map<string, number>();
    /** 内部订阅集合。 */
    private readonly subscriptions: Subscription[] = [];
    /** 排队中的草图重建请求。 */
    private readonly rebuildQueue: QueuedSketchRebuildRequest[] = [];
    /** 当前是否正在处理重建队列。 */
    private isProcessingQueue = false;
    /** 调度器是否已经释放。 */
    private isDisposed = false;
    /** 当前由阶段 B 显式控制顺序的引用传播轮次。 */
    private activeTimelinePropagationId: string | null = null;
    /** 外部草图定义变更版本，用于使异步冻结输入失效。 */
    private sketchMutationVersion = 0;
    /** 同一同步恢复批次的模型引用变化，微任务后才读取最终状态。 */
    private readonly pendingModelReferenceIds = new Set<string>();
    /** SDK 自动重建与宿主事务确认共用的可等待任务。 */
    private modelReferenceRebuildPromise: Promise<void> | null = null;

    /**
     * 构造函数。
     * @param viewer Viewer 实例。
     */
    public constructor(
        viewer: Viewer,
        runtime: FeatureExecutionRuntime,
        timelineExecutor?: FeatureTimelineRebuildExecutor,
    ) {
        this.viewer = viewer;
        this.runtime = runtime;
        this.timelineExecutor = timelineExecutor;
        this.dependencyGraph = new FeatureDependencyGraph();
        this.bindSketchDefinitionChanges();
    }

    /**
     * 释放资源。
     */
    public dispose(): void {
        if (this.isDisposed) {
            return;
        }
        this.isDisposed = true;
        this.pendingModelReferenceIds.clear();
        this.subscriptions.forEach((subscription) => subscription.unsubscribe());
        this.subscriptions.length = 0;
        this.dirtySketchRevisionMap.clear();
        const disposeError = new Error('FeatureRebuildOrchestrator 已释放');
        this.rebuildQueue.forEach((request) => request.reject(disposeError));
        this.rebuildQueue.length = 0;
        this.dependencyGraph.clear();
    }

    /**
     * 获取指定草图的下游特征 ID 列表。
     * @param sketchId 草图 ID。
     * @returns 特征 ID 列表。
     */
    public getDependentFeatureIds(sketchId: string): string[] {
        return this.queryDependentFeatureIds(sketchId);
    }

    /**
     * 正式基准引用变更后自动合并重建，历史恢复先删后建不会求值中间态。
     * @param referenceId 稳定模型引用。 @param isCurrent 文档/加载代次校验。
     */
    public notifyModelReferenceChanged(referenceId: string, isCurrent: () => boolean): void {
        if (this.isDisposed || this.dependencyGraph.getDependentModelReferenceFeatureIds(referenceId).length === 0) return;
        this.pendingModelReferenceIds.add(referenceId);
        if (this.modelReferenceRebuildPromise) return;
        this.modelReferenceRebuildPromise = Promise.resolve().then(async () => {
            while (!this.isDisposed && this.pendingModelReferenceIds.size > 0) {
                this.pendingModelReferenceIds.clear();
                if (!isCurrent()) return;
                // 复用正式串行时间线与 stopped 失败状态，不另起 Worker 写入通道。
                const result = await this.restoreLoadedTimeline(isCurrent);
                if (result.status !== 'success' && result.status !== 'stopped' && result.status !== 'stale') {
                    throw new Error(`基准引用变更后的特征重建失败：${result.status}`);
                }
            }
        }).finally(() => {
            this.modelReferenceRebuildPromise = null;
            const pendingId = this.pendingModelReferenceIds.values().next().value;
            if (pendingId && !this.isDisposed) this.notifyModelReferenceChanged(pendingId, isCurrent);
        });
        // 同步 SDK 调用仍有明确诊断；需要事务确认的宿主 await 原任务并沿原回滚路径处理。
        void this.modelReferenceRebuildPromise.catch((error) => console.error('[FeatureRebuild] 基准引用重建失败', referenceId, error));
    }

    /** @returns 等待本同步批次的基准引用重建完成；供模型事务和 SDK 调用方确认。 */
    public async flushModelReferenceChanges(): Promise<void> {
        while (this.modelReferenceRebuildPromise) await this.modelReferenceRebuildPromise;
    }

    /**
     * 查询依赖指定草图的下游特征。
     *
     * 设计说明：
     * 1. 依赖图按特征绑定中的草图身份建索引；新绑定统一锚定草图持久 ID，
     *    而运行时事件（草图 dirty/committed）携带的是会话内 uuid，必须在查询侧换算；
     * 2. 绑定内部只保存一种身份，兼容性由查询侧对“运行时 uuid + 持久 ID”取并集承担，
     *    使历史载荷（旧绑定存 uuid）与新载荷共用同一条重建链路；
     * 3. 草图已不存在或查询入口不可用时按原值查询，保持既有行为。
     *
     * @param sketchId 运行时 uuid 或草图持久 ID。
     * @returns 去重排序后的下游特征 ID 列表。
     */
    private queryDependentFeatureIds(sketchId: string): string[] {
        const queryKeys = new Set<string>([
            sketchId,
            this.resolvePersistentSketchId(sketchId),
        ]);

        const featureIds = new Set<string>();
        queryKeys.forEach((queryKey) => {
            this.dependencyGraph.getDependentFeatureIds(queryKey).forEach((featureId) => {
                featureIds.add(featureId);
            });
        });
        return [...featureIds].sort();
    }

    /**
     * 把草图运行时 ID 归一为命令与引用仓库使用的持久 ID。
     *
     * @param sketchId 草图运行时 ID 或持久 ID。
     * @returns 可用于引用依赖匹配的持久 ID；草图已不存在时保留原值。
     */
    private resolvePersistentSketchId(sketchId: string): string {
        const sketchGroupManager = this.viewer.sketchGroupManager;
        const lookup = sketchGroupManager ? Reflect.get(sketchGroupManager, 'getGroupById') : null;
        if (typeof lookup !== 'function') {
            return sketchId;
        }
        const group = lookup.call(sketchGroupManager, sketchId) as {persistentId?: string} | null;
        return group?.persistentId ?? sketchId;
    }

    /**
     * 把已经受影响的草图沿 sketch-entity 引用扩展为完整下游闭包。
     *
     * @param snapshot 冻结的引用快照。
     * @param affectedSketchIds 已确认受影响的草图集合。
     * @param relevantReferenceIds 本轮需要执行的引用集合。
     */
    private expandSketchReferenceClosure(
        snapshot: TimelineReferenceSnapshot,
        affectedSketchIds: Set<string>,
        relevantReferenceIds: Set<string>,
    ): void {
        let didExpand = true;
        while (didExpand) {
            didExpand = false;
            snapshot.items.forEach((item) => {
                if (
                    item.sourceDescriptor.kind !== 'sketch-entity'
                    || !affectedSketchIds.has(item.sourceDescriptor.sourceSketchId)
                ) {
                    return;
                }
                relevantReferenceIds.add(item.referenceId);
                if (!affectedSketchIds.has(item.targetSketchId)) {
                    affectedSketchIds.add(item.targetSketchId);
                    didExpand = true;
                }
            });
        }
    }

    /**
     * 计算当前草图变更真正能够到达的 Convert Entities 引用闭包。
     *
     * 设计说明：
     * 1. 草图来源引用按 sourceSketchId 向目标草图递归传播，覆盖 A→B→D；
     * 2. 首个草图消费者之后的 Feature 结果可能继续成为 topology 来源，
     *    因此只纳入该后缀中的 feature-result 引用，不触碰更早或外部模型引用；
     * 3. 缩放会改变目标草图工作空间，需要额外把“当前草图作为目标”的引用纳入本轮；
     * 4. 返回快照仍保持原捕获顺序，供 Executor 按真实时间线建立 checkpoint。
     *
     * @param sketchId 本次提交的草图运行时 ID 或持久 ID。
     * @param reason 草图提交原因。
     * @param fullCommands 完整 Feature 命令历史。
     * @param snapshot 执行前冻结的文档引用快照。
     * @returns 本轮首个受影响 Feature 与引用子集。
     */
    private createTimelineReferencePlan(
        sketchId: string,
        reason: string | undefined,
        fullCommands: readonly CadCommand[],
        snapshot: TimelineReferenceSnapshot,
    ): FeatureTimelineReferencePlan {
        const persistentSketchId = this.resolvePersistentSketchId(sketchId);
        const affectedSketchIds = new Set<string>([persistentSketchId]);
        const relevantReferenceIds = new Set<string>();

        if (reason === SKETCH_SCALE_ENTITIES_COMMIT_REASON) {
            snapshot.items.forEach((item) => {
                if (item.targetSketchId === persistentSketchId) {
                    relevantReferenceIds.add(item.referenceId);
                }
            });
        }

        this.expandSketchReferenceClosure(
            snapshot,
            affectedSketchIds,
            relevantReferenceIds,
        );

        const resolveFirstConsumerIndex = (): number => fullCommands.findIndex((command) => (
            this.dependencyGraph
                .getBindingsByFeatureId(command.id)
                .some((binding) => [...affectedSketchIds].some((affectedSketchId) => (
                    binding.dependsOnSketch(affectedSketchId)
                )))
        ));
        let cutoffFeatureIndex = resolveFirstConsumerIndex();

        /**
         * 从首个受影响 Feature 开始，后续 feature-result 都可能承接前序实体。
         * 当前依赖图尚不表达 Feature→Feature 的完整传递边，因此这里以历史后缀作为
         * 保守且有序的边界；仍只纳入 Convert Entities 引用，不改变普通 Feature 语义。
         */
        if (cutoffFeatureIndex >= 0) {
            const affectedFeatureIds = new Set(
                fullCommands.slice(cutoffFeatureIndex).map((command) => command.id),
            );
            snapshot.items.forEach((item) => {
                if (
                    item.sourceDescriptor.kind !== 'topology-edge'
                    || item.sourceDescriptor.owner.ownerKind !== 'feature-result'
                    || !affectedFeatureIds.has(item.sourceDescriptor.owner.ownerId)
                    || (
                        reason !== SKETCH_SCALE_ENTITIES_COMMIT_REASON
                        && item.targetSketchId === persistentSketchId
                    )
                ) {
                    return;
                }
                /**
                 * 普通拖拽、尺寸和属性提交只改变当前草图定义，不改变其外部来源。
                 * 当前草图自身的 Face/Loop 引用如果因 owner 位于 Feature 后缀而被重新加入，
                 * checkpoint 会在来源 Feature 尚未执行时查询该面，并把有效引用误写为 dangling。
                 * 缩放会改变目标投影空间，已由上方显式纳入当前目标引用，因此继续允许处理。
                 */
                relevantReferenceIds.add(item.referenceId);
                affectedSketchIds.add(item.targetSketchId);
            });

            this.expandSketchReferenceClosure(
                snapshot,
                affectedSketchIds,
                relevantReferenceIds,
            );
            cutoffFeatureIndex = resolveFirstConsumerIndex();
        }

        return {
            cutoffFeatureIndex: cutoffFeatureIndex >= 0
                ? cutoffFeatureIndex
                : fullCommands.length,
            referenceSnapshot: {
                items: snapshot.items
                    .filter((item) => relevantReferenceIds.has(item.referenceId))
                    .map((item) => structuredClone(item)),
            },
        };
    }

    /**
     * 使用最新命令历史同步依赖图缓存。
     * @param commands 命令历史。
     */
    public syncFromCommandHistory(commands: CadCommand[]): void {
        this.dependencyGraph.rebuildFromCommands(commands);
    }

    /**
     * 在离线包草图、Record 与权威时间线全部就位后重建完整文档。
     *
     * 该入口只把加载任务加入现有串行重建队列；实际求值继续复用同一个
     * FeatureTimelineRebuildExecutor、checkpoint 与结果应用链路。
     *
     * @param isRestoreCurrent 当前加载任务是否仍属于 Viewer 的活动文档代次。
     * @returns 加载文档的时间线重建结果。
     */
    public restoreLoadedTimeline(
        isRestoreCurrent: () => boolean = () => true,
    ): Promise<FeatureRebuildResult> {
        if (this.isDisposed) {
            return Promise.reject(new Error('FeatureRebuildOrchestrator 已释放'));
        }
        return new Promise<FeatureRebuildResult>((resolve, reject) => {
            this.rebuildQueue.push({
                kind: 'loaded',
                sketchId: 'puppy-loaded-document',
                revision: 0,
                isRestoreCurrent,
                resolve,
                reject,
            });
            if (!this.isProcessingQueue) {
                this.isProcessingQueue = true;
                void this.processRebuildQueue();
            }
        });
    }

    /**
     * 提交草图定义变更，并触发下游特征重建。
     * @param sketchId 草图 ID。
     * @returns 重建结果。
     */
    public async commitSketchDefinitionChanges(sketchId: string): Promise<FeatureRebuildResult> {
        const requestedSketch = this.viewer.sketchGroupManager?.getGroupById(sketchId) ?? null;
        const requestedRevision = requestedSketch?.definitionRevision;
        const result = await this.enqueueSketchRebuild(sketchId);
        const currentSketch = this.viewer.sketchGroupManager?.getGroupById(sketchId) ?? null;
        if (
            (result.status === 'success' || result.status === 'stopped')
            && currentSketch !== null
            && currentSketch === requestedSketch
            && currentSketch.definitionRevision === requestedRevision
        ) {
            /**
             * 显式提交入口由退出草图调用。即使没有任何下游 Feature，也必须确认当前
             * definition revision；否则后续 Convert Entities checkpoint 会把已退出草图
             * 误判为仍含未提交用户事务。等待期间出现的新 revision 不属于本次提交，
             * 必须继续保持 dirty，禁止被旧请求的成功结果一并吞掉。
             */
            currentSketch.acknowledgeDefinitionCommit();
        }
        return result;
    }

    /**
     * 在严格 Feature 前缀草图会话结束后恢复完整命令链。
     *
     * 实现约束：
     * 1. 完整历史必须来自 `input.fullCommands`，不能从当前 Worker 前缀反推；
     * 2. `cutoffFeatureIndex` 之前的命令保持原样，只重写完整后缀中的实时引用；
     * 3. 实际求值沿用 Rollback Bar 语义：清空 Worker 后从头重放正式 active commands；
     * 4. 当前 Worker 前缀只作为 replay 失败时的恢复快照，不作为增量求值基线；
     * 5. 该入口必须复用本类现有 revision、binding rewrite、replay 与 Mesh 同步能力，
     *    不得创建第二套队列或阶段 B topology 工作区。
     *
     * @param input 严格前缀退出重建输入。
     * @returns 完整恢复结果。
     */
    public async rebuildSketchAfterPrefixEdit(
        input: SketchPrefixEditRebuildInput,
    ): Promise<FeatureRebuildResult> {
        return this.enqueueSketchPrefixEditRebuild(input);
    }

    /**
     * 将草图重建请求加入串行队列。
     * @param sketchId 草图 ID。
     * @returns 重建结果。
     */
    private enqueueSketchRebuild(
        sketchId: string,
        reason?: string,
    ): Promise<FeatureRebuildResult> {
        if (this.isDisposed) {
            return Promise.reject(new Error('FeatureRebuildOrchestrator 已释放'));
        }
        const revision = this.viewer.sketchGroupManager?.getGroupById(sketchId)?.definitionRevision ?? 0;
        return new Promise<FeatureRebuildResult>((resolve, reject) => {
            this.rebuildQueue.push({
                kind: 'committed',
                sketchId,
                revision,
                reason,
                resolve,
                reject,
            });
            if (!this.isProcessingQueue) {
                this.isProcessingQueue = true;
                void this.processRebuildQueue();
            }
        });
    }

    /**
     * 将严格前缀退出恢复加入现有草图重建队列。
     *
     * 该入口不能直接调用 Worker，否则可能与 committed 事件触发的普通重建并发，
     * 使较旧 history epoch 覆盖草图退出时的最终定义。
     *
     * @param input 严格前缀退出重建输入。
     * @returns 重建结果。
     */
    private enqueueSketchPrefixEditRebuild(
        input: SketchPrefixEditRebuildInput,
    ): Promise<FeatureRebuildResult> {
        if (this.isDisposed) {
            return Promise.reject(new Error('FeatureRebuildOrchestrator 已释放'));
        }
        return new Promise<FeatureRebuildResult>((resolve, reject) => {
            this.rebuildQueue.push({
                kind: 'prefix-exit',
                sketchId: input.sketchId,
                revision: input.expectedDefinitionRevision,
                prefixEditInput: {
                    ...input,
                    fullCommands: input.fullCommands.map((command) => structuredClone(command)),
                },
                resolve,
                reject,
            });
            if (!this.isProcessingQueue) {
                this.isProcessingQueue = true;
                void this.processRebuildQueue();
            }
        });
    }

    /**
     * 串行处理重建队列。
     */
    private async processRebuildQueue(): Promise<void> {
        while (!this.isDisposed && this.rebuildQueue.length > 0) {
            const request = this.rebuildQueue.shift();
            if (!request) {
                continue;
            }
            try {
                const hasNewerQueuedRequest = this.rebuildQueue.some((queuedRequest) => {
                    return request.kind === 'committed'
                        && queuedRequest.kind === 'committed'
                        && queuedRequest.sketchId === request.sketchId
                        && queuedRequest.revision > request.revision;
                });
                if (hasNewerQueuedRequest) {
                    request.resolve({
                        success: true,
                        status: 'stale',
                        affectedFeatureIds: this.queryDependentFeatureIds(request.sketchId),
                    });
                    continue;
                }

                let result = request.kind === 'loaded'
                    ? await this.executeLoadedTimelineRestore(request.isRestoreCurrent)
                    : request.kind === 'prefix-exit'
                        ? await this.executeQueuedSketchPrefixEditRebuild(request)
                        : await this.executeSketchRebuild(
                            request.sketchId,
                            request.revision,
                            request.reason,
                        );
                /**
                 * stale 既可能由 Worker guard 抛出异常，也可能由时间线执行器作为正常结果返回。
                 * 两种表达必须在同一个队列边界进入相同的一次补偿，否则结果型 stale 会被误当成
                 * 已完成请求，导致草图退出报错或 mouseup 后下游 Feature 保留旧结果。
                 */
                if (result.status === 'stale') {
                    result = request.kind === 'loaded'
                        ? await this.recoverStaleLoadedTimelineRestore(request.isRestoreCurrent)
                        : request.kind === 'prefix-exit'
                            ? await this.recoverStaleSketchPrefixEditRebuild(request)
                            : await this.recoverStaleSketchRebuild(
                                request.sketchId,
                                request.reason,
                            );
                }
                request.resolve(result);
            } catch (error) {
                try {
                    if (isStaleWorkerResponseError(error)) {
                        if (request.kind === 'loaded') {
                            request.resolve(await this.recoverStaleLoadedTimelineRestore(
                                request.isRestoreCurrent,
                            ));
                            continue;
                        }
                        const recoveredResult = request.kind === 'prefix-exit'
                            ? await this.recoverStaleSketchPrefixEditRebuild(request)
                            : await this.recoverStaleSketchRebuild(
                                request.sketchId,
                                request.reason,
                            );
                        request.resolve(recoveredResult);
                        continue;
                    }
                    throw error;
                } catch (finalError) {
                    if (request.kind !== 'loaded') {
                        this.recordDirtySketchRevision(request.sketchId, request.revision);
                    }
                    request.reject(
                        finalError instanceof Error
                            ? finalError
                            : new Error(String(finalError)),
                    );
                }
            }
        }
        if (this.isDisposed) {
            this.dirtySketchRevisionMap.clear();
            this.dependencyGraph.clear();
        }
        this.isProcessingQueue = false;
    }

    /**
     * 执行队列中的严格前缀退出恢复。
     *
     * 草图仍存在时采用 Manager 中的最新 revision；草图因 discardEmpty 被删除时，
     * 结束结果携带的 revision 仍是本次退出边界的权威版本。
     *
     * @param request 严格前缀退出队列请求。
     * @returns 重建结果。
     */
    private async executeQueuedSketchPrefixEditRebuild(
        request: QueuedSketchRebuildRequest,
    ): Promise<FeatureRebuildResult> {
        const input = request.prefixEditInput;
        if (!input) {
            throw new Error(`严格前缀退出请求缺少重建输入：${request.sketchId}`);
        }
        const currentRevision = this.viewer.sketchGroupManager
            ?.getGroupById(request.sketchId)
            ?.definitionRevision;
        return this.executeSketchPrefixEditRebuild({
            ...input,
            expectedDefinitionRevision: typeof currentRevision === 'number'
                ? currentRevision
                : input.expectedDefinitionRevision,
        });
    }

    /**
     * 冻结加载完成后的权威命令、草图身份和全部引用，再进入现有阶段 B 执行链。
     *
     * @param isRestoreCurrent 当前加载任务的文档代次 guard。
     * @returns 完整文档时间线重建结果。
     */
    private async executeLoadedTimelineRestore(
        isRestoreCurrent: () => boolean = () => true,
    ): Promise<FeatureRebuildResult> {
        if (!isRestoreCurrent()) {
            return {
                success: true,
                status: 'stale',
                affectedFeatureIds: [],
            };
        }
        const beforeSummary = await this.runtime.getGeometryStateSummary();
        if (!isRestoreCurrent()) {
            return {
                success: true,
                status: 'stale',
                affectedFeatureIds: [],
            };
        }
        const featureTreeSummary = await this.viewer.getFeatureTreeStateSummary();
        const sketches = this.viewer.listSketchGroupSummaries();
        const referenceSnapshot = this.viewer.captureSketchEntityReferenceTimelineSnapshot(
            sketches.map((sketch) => sketch.persistentId),
        );

        return this.executeSketchPrefixEditRebuild(
            {
                sketchId: 'puppy-loaded-document',
                fullCommands: featureTreeSummary.history,
                cutoffFeatureIndex: 0,
                expectedDefinitionRevision: 0,
            },
            referenceSnapshot,
            beforeSummary,
            undefined,
            isRestoreCurrent,
        );
    }

    /**
     * loaded Timeline 首次过期后等待当前任务边界落定，并按同一恢复代次重试一次。
     *
     * @param isRestoreCurrent 当前加载任务的文档代次 guard。
     * @returns 第二次完整文档重建结果。
     */
    private async recoverStaleLoadedTimelineRestore(
        isRestoreCurrent: () => boolean = () => true,
    ): Promise<FeatureRebuildResult> {
        await this.waitForNextTask();
        return this.executeLoadedTimelineRestore(isRestoreCurrent);
    }

    /**
     * 在 stale Worker 响应后按最新草图版本补偿一次严格前缀退出恢复。
     *
     * 第二次仍 stale 时转换为普通错误交给 Viewer，避免 SketchSessionTerminator
     * 把 stale 当作可忽略结果并永久留下 closing 状态。
     *
     * @param request 严格前缀退出队列请求。
     * @returns 重建结果。
     */
    private async recoverStaleSketchPrefixEditRebuild(
        request: QueuedSketchRebuildRequest,
    ): Promise<FeatureRebuildResult> {
        await this.waitForNextTask();
        try {
            const result = await this.executeQueuedSketchPrefixEditRebuild(request);
            if (result.status === 'stale') {
                throw new Error(`严格前缀退出恢复连续返回过期结果：${request.sketchId}`);
            }
            return result;
        } catch (error) {
            if (!isStaleWorkerResponseError(error)) {
                throw error;
            }
            throw new Error(`严格前缀退出恢复连续收到过期 Worker 响应：${request.sketchId}`);
        }
    }

    /**
     * 从进入编辑前捕获的完整命令快照恢复正式 FeatureManager 状态。
     *
     * @param input 严格前缀退出重建输入。
     * @returns 重建结果。
     */
    private async executeSketchPrefixEditRebuild(
        input: SketchPrefixEditRebuildInput,
        preparedReferenceSnapshot?: TimelineReferenceSnapshot,
        preparedBeforeSummary?: WorkerGeometryStateSummary,
        commitReason?: string,
        isExternalInputCurrent: () => boolean = () => true,
    ): Promise<FeatureRebuildResult> {
        if (
            !Number.isInteger(input.cutoffFeatureIndex)
            || input.cutoffFeatureIndex < 0
            || input.cutoffFeatureIndex > input.fullCommands.length
        ) {
            throw new Error(
                `严格前缀截止位置无效：${input.cutoffFeatureIndex}/${input.fullCommands.length}`,
            );
        }
        const affectedFeatureIds = input.fullCommands
            .slice(input.cutoffFeatureIndex)
            .map((command) => command.id);
        if (!isExternalInputCurrent()) {
            return {
                success: true,
                status: 'stale',
                affectedFeatureIds,
            };
        }

        const sketches = this.viewer.listSketchGroupSummaries().map((sketch) => ({
            runtimeId: sketch.id,
            persistentId: sketch.persistentId,
        }));
        const capturedReferenceSnapshot = preparedReferenceSnapshot
            ?? this.viewer.captureSketchEntityReferenceTimelineSnapshot(
                sketches.map((sketch) => sketch.persistentId),
            );
        this.dependencyGraph.rebuildFromCommands(input.fullCommands);
        const referenceSnapshot = preparedReferenceSnapshot
            ? capturedReferenceSnapshot
            : this.createTimelineReferencePlan(
                input.sketchId,
                commitReason,
                input.fullCommands,
                capturedReferenceSnapshot,
            ).referenceSnapshot;

        /**
         * 没有 Feature 后缀且当前草图也没有 Convert Entities 下游时，
         * Worker 已等于正式命令前缀，无需做空收益 replay。
         * A→B 但没有 Feature C 的场景仍继续执行，以便到达 B checkpoint。
         */
        if (
            affectedFeatureIds.length <= 0
            && referenceSnapshot.items.length <= 0
        ) {
            this.clearDirtySketchRevision(
                input.sketchId,
                input.expectedDefinitionRevision,
            );
            return {
                success: true,
                status: 'success',
                affectedFeatureIds: [],
            };
        }

        const beforeSummary = preparedBeforeSummary
            ?? await this.runtime.getGeometryStateSummary();
        const rollbackState = this.viewer.getFeatureRollbackState();
        const initialWorkerRevision = this.runtime.getWorkerRevisionState();
        const frozenMutationVersion = this.sketchMutationVersion;
        const frozenReferenceIds = referenceSnapshot.items
            .map((item) => item.referenceId)
            .sort();
        const frozenReferenceIdSet = new Set(frozenReferenceIds);
        const checkpointRemovedReferenceIdSet = new Set<string>();
        const propagationId = this.createTimelinePropagationId();
        const activeFeatureIdSet = new Set(rollbackState.activeFeatureIds);
        const evaluationCommands = input.fullCommands.filter((command) => (
            activeFeatureIdSet.has(command.id)
        ));

        /**
         * 依赖图仍是现有 live binding 的唯一来源。Executor 只决定“何时重写”，
         * 不新增拉伸、旋转、圆角或倒角的第二套重写规则。
         */
        const executor = this.timelineExecutor ?? new FeatureTimelineRebuildExecutor({
            executeFeaturePrefixBatch: payload => this.runtime.executeFeaturePrefixBatch(payload),
            queryTopologyCheckpoint: payload => this.runtime.queryTopologyCheckpoint(payload),
            rebuildCheckpoint: async checkpoint => {
                const result = await this.viewer
                    .rebuildSketchEntityReferenceTimelineCheckpoint(checkpoint);
                /**
                 * 面边界变化会由 checkpoint 合法移除已经消失的成员引用。
                 * 这些删除属于本轮阶段 B 的输出，不能再被输入冻结校验误判为外部修改。
                 */
                result.removedReferenceIds.forEach((referenceId) => (
                    checkpointRemovedReferenceIdSet.add(referenceId)
                ));
                return result;
            },
            rewriteCommand: command => {
                const commandIndex = input.fullCommands.findIndex((item) => item.id === command.id);
                return commandIndex < input.cutoffFeatureIndex
                    ? structuredClone(command)
                    : this.rewriteCommandWithLiveBindings(command).command;
            },
            readWorkerRevision: () => this.runtime.getWorkerRevisionState(),
            isInputCurrent: () => {
                if (!isExternalInputCurrent()) {
                    return false;
                }
                if (this.sketchMutationVersion !== frozenMutationVersion) {
                    return false;
                }
                const currentRollbackState = this.viewer.getFeatureRollbackState();
                if (
                    currentRollbackState.cursorIndex !== rollbackState.cursorIndex
                    || !this.areJsonLikeValuesEqual(
                        currentRollbackState.activeFeatureIds,
                        rollbackState.activeFeatureIds,
                    )
                    || !this.areJsonLikeValuesEqual(
                        currentRollbackState.timelineItems,
                        rollbackState.timelineItems,
                    )
                ) {
                    return false;
                }
                const currentReferenceIds = this.viewer
                    .captureSketchEntityReferenceTimelineSnapshot(
                        sketches.map((sketch) => sketch.persistentId),
                    )
                    .items
                    .map((item) => item.referenceId)
                    .filter((referenceId) => frozenReferenceIdSet.has(referenceId))
                    .sort();
                const expectedReferenceIds = frozenReferenceIds.filter((referenceId) => (
                    !checkpointRemovedReferenceIdSet.has(referenceId)
                ));
                return this.areJsonLikeValuesEqual(
                    currentReferenceIds,
                    expectedReferenceIds,
                );
            },
        });

        this.activeTimelinePropagationId = propagationId;
        let timelineResult: FeatureTimelineRebuildResult;
        try {
            timelineResult = await executor.execute({
                fullCommands: input.fullCommands,
                evaluationCommands,
                timelineItems: rollbackState.timelineItems,
                rollbackCursorIndex: rollbackState.cursorIndex,
                sketches,
                referenceSnapshot,
                propagationId,
                initialWorkerRevision,
            });
        } finally {
            this.activeTimelinePropagationId = null;
        }

        if (timelineResult.status === 'success' || timelineResult.status === 'stopped') {
            await this.viewer.applyFeatureTimelineRebuildResult({
                beforeSummary,
                result: timelineResult,
                rollbackState,
                affectedFeatureIds,
            });
            this.dependencyGraph.rebuildFromCommands(timelineResult.fullCommands);
            this.clearDirtySketchRevision(
                input.sketchId,
                input.expectedDefinitionRevision,
            );
            return {
                success: true,
                status: timelineResult.status,
                affectedFeatureIds,
                error: timelineResult.error,
                warnings: timelineResult.warnings,
            };
        }

        return {
            success: timelineResult.status === 'stale',
            status: timelineResult.status,
            affectedFeatureIds,
            error: timelineResult.error,
            warnings: timelineResult.warnings,
        };
    }

    /**
     * 创建单次阶段 B 执行标识。
     *
     * @returns 当前文档内足够唯一的传播 ID。
     */
    private createTimelinePropagationId(): string {
        const cryptoApi = globalThis.crypto;
        if (cryptoApi && typeof cryptoApi.randomUUID === 'function') {
            return `feature-timeline-rebuild-${cryptoApi.randomUUID()}`;
        }
        return `feature-timeline-rebuild-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    }

    /**
     * 判断草图事件是否由当前阶段 B checkpoint 自身产生。
     *
     * @param reason 草图定义提交原因。
     * @param propagationId 引用传播轮次。
     * @param definitionChangeOwnerId 底层定义写入归属。
     * @returns 事件已由当前 Executor 显式排序时返回 true。
     */
    private isActiveTimelineCheckpointEvent(
        reason: string | undefined,
        propagationId: string | undefined,
        definitionChangeOwnerId?: string,
    ): boolean {
        const activePropagationId = this.activeTimelinePropagationId;
        if (!activePropagationId) {
            return false;
        }

        return (
            reason === REFERENCE_TARGET_REBUILT_COMMIT_REASON
            && propagationId === activePropagationId
        ) || definitionChangeOwnerId === activePropagationId;
    }

    /**
     * 在 Worker history epoch 淘汰重建请求后补偿一次。
     *
     * 设计说明：
     * 1. 鼠标释放会同时提交草图定义并推进 history epoch，首个重建响应常被判为 stale；
     *    这与 `refreshEditSketchFeaturePreviewForCommit` 的预览重试是同一根因，只是走重建链路；
     * 2. 等一拍让 epoch 落定后按最新 revision 重试一次，成功即把编辑后的轮廓落到下游特征；
     * 3. 若仍 stale，保留 dirty 记录，让后续 committed / 退出草图仍有补偿机会，
     *    绝不静默丢弃这次尺寸更新。
     *
     * @param sketchId 草图 ID。
     * @returns 重建结果。
     */
    private async recoverStaleSketchRebuild(
        sketchId: string,
        reason?: string,
    ): Promise<FeatureRebuildResult> {
        await this.waitForNextTask();
        const latestRevision = this.viewer.sketchGroupManager?.getGroupById(sketchId)?.definitionRevision ?? 0;
        try {
            const result = await this.executeSketchRebuild(sketchId, latestRevision, reason);
            if (result.status === 'stale') {
                this.recordDirtySketchRevision(sketchId, latestRevision);
            }
            return result;
        } catch (error) {
            if (!isStaleWorkerResponseError(error)) {
                throw error;
            }
            this.recordDirtySketchRevision(sketchId, latestRevision);
            return {
                success: true,
                status: 'stale',
                affectedFeatureIds: this.queryDependentFeatureIds(sketchId),
            };
        }
    }

    /**
     * 执行一次草图依赖重建。
     * @param sketchId 草图 ID。
     * @param revision 本次重建请求对应的草图定义版本。
     * @returns 重建结果。
     */
    private async executeSketchRebuild(
        sketchId: string,
        revision: number,
        reason?: string,
    ): Promise<FeatureRebuildResult> {
        const stateSummary = await this.runtime.getGeometryStateSummary();
        const sketches = this.viewer.listSketchGroupSummaries();
        const capturedReferenceSnapshot =
            this.viewer.captureSketchEntityReferenceTimelineSnapshot(
                sketches.map((sketch) => sketch.persistentId),
            );

        /**
         * 只有文档中存在 Convert Entities 时才读取完整 FeatureManager 历史并规划 checkpoint。
         * 无引用文档继续沿用下方现有单批次 replay，不承担阶段 B 的快照与分段成本。
         */
        if (capturedReferenceSnapshot.items.length > 0) {
            const planningMutationVersion = this.sketchMutationVersion;
            const featureTreeSummary = await this.viewer.getFeatureTreeStateSummary();
            if (this.sketchMutationVersion !== planningMutationVersion) {
                return {
                    success: true,
                    status: 'stale',
                    affectedFeatureIds: this.queryDependentFeatureIds(sketchId),
                };
            }
            const currentSketches = this.viewer.listSketchGroupSummaries();
            const currentReferenceSnapshot =
                this.viewer.captureSketchEntityReferenceTimelineSnapshot(
                    currentSketches.map((sketch) => sketch.persistentId),
                );
            this.dependencyGraph.rebuildFromCommands(featureTreeSummary.history);
            const timelinePlan = this.createTimelineReferencePlan(
                sketchId,
                reason,
                featureTreeSummary.history,
                currentReferenceSnapshot,
            );
            if (timelinePlan.referenceSnapshot.items.length > 0) {
                return this.executeSketchPrefixEditRebuild(
                    {
                        sketchId,
                        fullCommands: featureTreeSummary.history,
                        cutoffFeatureIndex: timelinePlan.cutoffFeatureIndex,
                        expectedDefinitionRevision: revision,
                    },
                    timelinePlan.referenceSnapshot,
                    stateSummary,
                    reason,
                );
            }
        }

        this.dependencyGraph.rebuildFromCommands(stateSummary.history);
        const affectedFeatureIds = this.queryDependentFeatureIds(sketchId);
        this.clearDirtySketchRevision(sketchId, revision);
        if (affectedFeatureIds.length <= 0) {
            return {
                success: true,
                status: 'success',
                affectedFeatureIds: [],
            };
        }

        const rewriteResults = stateSummary.history.map((command) => this.rewriteCommandWithLiveBindings(command));
        const rewrittenCommands = rewriteResults.map((result) => result.command);
        const hasCommandChanged = rewriteResults.some((result) => result.changed);
        if (!hasCommandChanged) {
            this.viewer.sketchGroupManager?.getGroupById(sketchId)?.acknowledgeDefinitionCommit();
            return {
                success: true,
                status: 'success',
                affectedFeatureIds,
            };
        }
        const targetFeatureId = affectedFeatureIds[affectedFeatureIds.length - 1];
        const replayResult = await this.runtime.replayFeatureCommands({
            commands: rewrittenCommands,
            clearFirst: true,
            targetFeatureId,
            rollbackCommands: stateSummary.history,
            syncHistoryProjection: true,
        });
        const nextSummary = replayResult.summary;
        this.dependencyGraph.rebuildFromCommands(nextSummary.history);
        this.viewer.sketchGroupManager?.getGroupById(sketchId)?.acknowledgeDefinitionCommit();
        await this.viewer.syncAffectedFeatureMeshesFromState({
            beforeSummary: stateSummary,
            afterSummary: nextSummary,
            affectedCommandIds: affectedFeatureIds,
        });

        return {
            success: true,
            status: 'success',
            affectedFeatureIds,
            consistency: replayResult.validation,
        };
    }

    /**
     * 记录草图 dirty revision。
     *
     * 设计说明：
     * 1. 草图拖拽释放后的刷新是异步 Worker 请求，旧请求可能被后续 history epoch 淘汰；
     * 2. 只记录 sketchId 会在旧请求回调里误清新的 dirty 状态；
     * 3. 因此这里按 revision 单调记录最新脏版本，清理时必须确认没有更新的脏版本覆盖。
     *
     * @param sketchId 草图 ID。
     * @param revision 草图定义版本。
     */
    private recordDirtySketchRevision(sketchId: string, revision: number): void {
        const currentRevision = this.dirtySketchRevisionMap.get(sketchId) ?? -1;
        this.dirtySketchRevisionMap.set(sketchId, Math.max(currentRevision, revision));
    }

    /**
     * 清理指定草图的 dirty revision。
     *
     * @param sketchId 草图 ID。
     * @param revision 已处理的草图定义版本；未传时清理整个草图记录。
     */
    private clearDirtySketchRevision(sketchId: string, revision?: number): void {
        if (typeof revision !== 'number') {
            this.dirtySketchRevisionMap.delete(sketchId);
            return;
        }
        const currentRevision = this.dirtySketchRevisionMap.get(sketchId);
        if (typeof currentRevision !== 'number' || currentRevision > revision) {
            return;
        }
        this.dirtySketchRevisionMap.delete(sketchId);
    }

    /**
     * 判断 committed 事件是否仍对当前 dirty revision 有效。
     *
     * @param sketchId 草图 ID。
     * @param revision committed 事件携带的草图定义版本。
     * @returns 当前事件是否仍需要处理。
     */
    private shouldHandleCommittedSketchRevision(sketchId: string, revision: number): boolean {
        const currentRevision = this.dirtySketchRevisionMap.get(sketchId);
        return typeof currentRevision === 'number' && currentRevision <= revision;
    }

    /**
     * 等待下一轮事件循环，让同一鼠标释放动作中推进的 history epoch 先完成。
     *
     * @returns 完成等待的 Promise。
     */
    private async waitForNextTask(): Promise<void> {
        await new Promise<void>((resolve) => {
            setTimeout(resolve, 0);
        });
    }

    /**
     * 在 committed 边界刷新编辑草图特征预览。
     *
     * 设计说明：
     * 1. 鼠标释放会同时提交草图定义和推进 history epoch，第一次 Worker 预览响应可能被判定为 stale；
     * 2. stale 不是几何失败，必须按最新 epoch 重试一次，保证释放后的最终轮廓能落到黄色预览体；
     * 3. 如果重试期间又出现更新的 dirty revision，则当前 committed 事件已经被新事件覆盖，不能清理 dirty。
     *
     * @param input committed 刷新输入。
     * @returns 编辑草图预览刷新结果。
     */
    private async refreshEditSketchFeaturePreviewForCommit(input: {
        sketchId: string;
        revision: number;
    }): Promise<EditSketchPreviewRefreshCommitResult> {
        try {
            const handled = await this.viewer.refreshEditSketchFeaturePreviewForSketch(input.sketchId);
            return {
                handled,
                shouldClearDirtyRevision: handled,
            };
        } catch (error) {
            if (!isStaleWorkerResponseError(error)) {
                throw error;
            }
        }

        await this.waitForNextTask();
        if (!this.shouldHandleCommittedSketchRevision(input.sketchId, input.revision)) {
            return {
                handled: true,
                shouldClearDirtyRevision: false,
            };
        }

        try {
            const handled = await this.viewer.refreshEditSketchFeaturePreviewForSketch(input.sketchId);
            return {
                handled,
                shouldClearDirtyRevision: handled,
            };
        } catch (error) {
            if (isStaleWorkerResponseError(error)) {
                return {
                    handled: true,
                    shouldClearDirtyRevision: false,
                };
            }
            throw error;
        }
    }

    /**
     * 处理草图定义提交事件。
     *
     * @param event 草图定义提交事件。
     */
    private async handleSketchDefinitionCommitted(event: SketchDefinitionCommittedEvent): Promise<void> {
        if (this.isActiveTimelineCheckpointEvent(event.reason, event.propagationId)) {
            return;
        }
        this.sketchMutationVersion += 1;
        if (event.reason === HISTORY_SKETCH_RUNTIME_RESTORE_COMMIT_REASON) {
            this.clearDirtySketchRevision(event.sketchId);
            return;
        }
        if (!this.shouldHandleCommittedSketchRevision(event.sketchId, event.revision)) {
            return;
        }

        const previewRefreshResult = await this.refreshEditSketchFeaturePreviewForCommit({
            sketchId: event.sketchId,
            revision: event.revision,
        });
        if (previewRefreshResult.handled) {
            if (previewRefreshResult.shouldClearDirtyRevision) {
                this.clearDirtySketchRevision(event.sketchId, event.revision);
            }
            return;
        }
        if (!this.shouldHandleCommittedSketchRevision(event.sketchId, event.revision)) {
            return;
        }

        void this.enqueueSketchRebuild(event.sketchId, event.reason).catch((error) => {
            console.error(`草图提交后触发特征重建失败: ${event.sketchId}`, error);
        });
    }

    /**
     * 绑定草图定义变化总线。
     * 说明：
     * 1. dirty 事件只记录草图定义已经变化；
     * 2. committed 事件代表鼠标释放、尺寸输入完成或退出草图等稳定边界，才触发依赖特征重建；
     * 3. 这样可以避免拖拽中频繁替换结果体导致闪烁，同时保持最终形状释放后自动重建。
     */
    private bindSketchDefinitionChanges(): void {
        const definitionChangeHub = this.viewer.sketchGroupManager?.definitionChangeHub;
        if (!definitionChangeHub) {
            return;
        }

        this.subscriptions.push(
            definitionChangeHub.observeDirty().subscribe((event) => {
                if (this.isActiveTimelineCheckpointEvent(
                    event.reason,
                    event.propagationId,
                    event.definitionChangeOwnerId,
                )) {
                    return;
                }
                this.sketchMutationVersion += 1;
                if (event.reason === HISTORY_SKETCH_RUNTIME_RESTORE_COMMIT_REASON) {
                    return;
                }
                this.recordDirtySketchRevision(event.sketchId, event.revision);
            }),
            definitionChangeHub.observeCommitted().subscribe((event) => {
                void this.handleSketchDefinitionCommitted(event).catch((error) => {
                    if (isStaleWorkerResponseError(error)) {
                        return;
                    }
                    console.error(`草图提交后刷新特征编辑预览失败: ${event.sketchId}`, error);
                });
            }),
        );
    }

    /**
     * 用当前 live binding 重写命令。
     * @param command 原始命令。
     * @returns 重写后的命令。
     */
    private rewriteCommandWithLiveBindings(command: CadCommand): RewriteCommandResult {
        if (command.type === 'extrude') {
            return this.rewriteExtrudeCommandWithLiveBindings(command);
        }
        if (command.type === 'revolve') {
            return this.rewriteRevolveCommandWithLiveBindings(command);
        }
        if (command.type === 'fillet') {
            return this.rewriteFilletCommandWithLiveBindings(command);
        }
        if (command.type === 'chamfer') {
            return this.rewriteChamferCommandWithLiveBindings(command);
        }
        return {
            command: structuredClone(command),
            changed: false,
        };
    }

    /**
     * 用 live binding 重写拉伸命令。
     *
     * @param command 原始拉伸命令。
     * @returns 重写结果。
     */
    private rewriteExtrudeCommandWithLiveBindings(command: CadCommand): RewriteCommandResult {
        const bindings = this.dependencyGraph.getBindingsByFeatureId(command.id);
        if (bindings.length <= 0) {
            return {
                command: structuredClone(command),
                changed: false,
            };
        }

        const nextCommand = structuredClone(command);
        const nextParams = {...(nextCommand.params as Record<string, unknown>)} as ExtrudeParams & Record<string, unknown>;
        let changed = false;

        bindings.forEach((binding) => {
            const resolved = binding.resolve({
                viewer: this.viewer,
            });
            const payload = binding.serialize();
            if (resolved?.kind === 'extrude-reference') {
                const key = resolved.role === 'extrude-direction1' ? 'direction1' : 'direction2';
                if (nextParams[key]) nextParams[key]!.target = resolved.target;
                changed = true;
                return;
            }
            if (payload.kind === 'model-topology' && payload.role?.startsWith('extrude-')) {
                const key = payload.role === 'extrude-direction1' ? 'direction1' : 'direction2';
                const direction = nextParams[key];
                if (!direction?.target || resolved?.kind !== 'model-topology' || !resolved.object) {
                    throw new Error(`拉伸终止引用无法重建：${payload.bodyObjectId}`);
                }
                if (direction.target.kind === 'model-topology') {
                    const selection = resolved.items[0];
                    if (!selection) throw new Error(`拉伸终止拓扑引用丢失：${payload.bodyObjectId}`);
                    direction.target = { kind: 'model-topology', selection };
                }
                changed = true;
                return;
            }
            if (!resolved || resolved.kind !== 'sketch-profile') {
                return;
            }

            changed = changed
                || nextParams.sourceType !== 'region_geometry'
                || nextParams.sketchId !== resolved.sketchId
                || !this.areJsonLikeValuesEqual(nextParams.contourKeys, resolved.contourKeys)
                || !this.areJsonLikeValuesEqual(nextParams.regionGeometry, resolved.regionGeometries[0])
                || !this.areJsonLikeValuesEqual(nextParams.regionGeometries, resolved.regionGeometries)
                || !this.areJsonLikeValuesEqual(nextParams.runtimeSketchPlane, resolved.runtimeSketchPlane)
                || !this.areJsonLikeValuesEqual(nextParams.runtimeSketchEntities, resolved.runtimeSketchEntities);

            nextParams.sourceType = 'region_geometry';
            nextParams.sketchId = resolved.sketchId;
            nextParams.contourKeys = [...resolved.contourKeys];
            nextParams.regionGeometry = resolved.regionGeometries[0];
            nextParams.regionGeometries = [...resolved.regionGeometries];
            nextParams.runtimeSketchPlane = resolved.runtimeSketchPlane;
            nextParams.runtimeSketchEntities = resolved.runtimeSketchEntities;
        });

        nextCommand.params = nextParams;
        return {
            command: nextCommand,
            changed,
        };
    }

    /**
     * 用 live binding 重写旋转命令。
     *
     * 设计说明：
     * 1. 旋转凸台同时依赖草图轮廓与草图轴；
     * 2. 草图发生变更后，不能继续使用旧命令中缓存的 regionGeometry 与 axis 数值；
     * 3. 这里通过统一 binding 回读最新草图对象，确保 rebuild 与创建/编辑链路使用同一份输入真相。
     *
     * @param command 原始旋转命令。
     * @returns 重写结果。
     */
    private rewriteRevolveCommandWithLiveBindings(command: CadCommand): RewriteCommandResult {
        const bindings = this.dependencyGraph.getBindingsByFeatureId(command.id);
        if (bindings.length <= 0) {
            return {
                command: structuredClone(command),
                changed: false,
            };
        }

        const nextCommand = structuredClone(command);
        const nextParams = {...(nextCommand.params as Record<string, unknown>)} as RevolveParams & Record<string, unknown>;
        const axisResolver = new RevolveAxisResolver();
        let changed = false;

        bindings.forEach((binding) => {
            const resolved = binding.resolve({
                viewer: this.viewer,
            });
            if (!resolved) {
                return;
            }

            if (resolved.kind === 'sketch-profile') {
                changed = changed
                    || nextParams.sourceType !== 'region_geometry'
                    || nextParams.sketchId !== resolved.sketchId
                    || !this.areJsonLikeValuesEqual(nextParams.contourKeys, resolved.contourKeys)
                    || !this.areJsonLikeValuesEqual(nextParams.regionGeometry, resolved.regionGeometries[0])
                    || !this.areJsonLikeValuesEqual(nextParams.regionGeometries, resolved.regionGeometries)
                    || !this.areJsonLikeValuesEqual(nextParams.runtimeSketchPlane, resolved.runtimeSketchPlane)
                    || !this.areJsonLikeValuesEqual(nextParams.runtimeSketchEntities, resolved.runtimeSketchEntities);

                nextParams.sourceType = 'region_geometry';
                nextParams.sketchId = resolved.sketchId;
                nextParams.contourKeys = [...resolved.contourKeys];
                nextParams.regionGeometry = resolved.regionGeometries[0];
                nextParams.regionGeometries = [...resolved.regionGeometries];
                nextParams.runtimeSketchPlane = resolved.runtimeSketchPlane;
                nextParams.runtimeSketchEntities = resolved.runtimeSketchEntities;
                return;
            }

            if (resolved.kind !== 'sketch-sub-entity') {
                return;
            }

            const axisResolution = axisResolver.resolveFromSketchBinding({
                object: resolved.object,
                sketchId: resolved.sketchId,
                preserveDirection: nextParams.axisReference?.preserveDirection,
            });
            if (!axisResolution) {
                return;
            }
            const nextAxis = nextParams.axisReference?.preserveDirection && nextParams.axis
                ? this.alignRevolveAxisDirection(axisResolution.axis, nextParams.axis)
                : axisResolution.axis;

            changed = changed
                || !this.areJsonLikeValuesEqual(nextParams.axis, nextAxis)
                || !this.areJsonLikeValuesEqual(nextParams.axisReference, axisResolution.axisReference);

            nextParams.axis = nextAxis;
            nextParams.axisReference = axisResolution.axisReference;
        });

        nextCommand.params = nextParams;
        return {
            command: nextCommand,
            changed,
        };
    }

    /**
     * 按持久化方向对齐旋转轴方向。
     *
     * 设计说明：
     * 1. SolidWorks 编辑草图后，旋转方向应保持用户创建特征时的语义；
     * 2. 草图线端点顺序可能因重建或复制发生反转；
     * 3. 因此当 axisReference 明确要求 preserveDirection 时，通过旧轴方向校正新轴方向。
     *
     * @param axis 最新解析轴。
     * @param persistedAxis 命令历史中的旧轴。
     * @returns 已对齐方向的旋转轴。
     */
    private alignRevolveAxisDirection(
        axis: RevolveParams['axis'],
        persistedAxis: RevolveParams['axis'],
    ): RevolveParams['axis'] {
        const dot = axis.direction[0] * persistedAxis.direction[0]
            + axis.direction[1] * persistedAxis.direction[1]
            + axis.direction[2] * persistedAxis.direction[2];
        if (dot >= 0) {
            return axis;
        }
        return {
            point: [...axis.point] as [number, number, number],
            direction: axis.direction.map((value) => {
                const nextValue = -value;
                return Object.is(nextValue, -0) ? 0 : nextValue;
            }) as [number, number, number],
        };
    }

    /**
     * 用 live binding 重写圆角命令。
     *
     * @param command 原始圆角命令。
     * @returns 重写结果。
     */
    private rewriteFilletCommandWithLiveBindings(command: CadCommand): RewriteCommandResult {
        const bindings = this.dependencyGraph.getBindingsByFeatureId(command.id);
        if (bindings.length <= 0) {
            return {
                command: structuredClone(command),
                changed: false,
            };
        }

        const nextCommand = structuredClone(command);
        const nextParams = {...(nextCommand.params as Record<string, unknown>)} as FilletParams & Record<string, unknown>;
        const liveResolution = this.resolveLiveModelTopologyBindings(bindings);
        if (!liveResolution) {
            nextCommand.params = nextParams;
            return {
                command: nextCommand,
                changed: false,
            };
        }

        const nextDefinition = this.rewriteFilletDefinitionWithLiveBindings(
            nextParams.definition,
            liveResolution.itemMap,
            liveResolution.pointMap,
        );
        const changed = !this.areJsonLikeValuesEqual(nextParams.definition, nextDefinition)
            || !this.areJsonLikeValuesEqual(nextParams.referenceBindings, liveResolution.serializedBindings);

        nextParams.definition = nextDefinition;
        nextParams.referenceBindings = liveResolution.serializedBindings;
        nextCommand.params = nextParams;
        return {
            command: nextCommand,
            changed,
        };
    }

    /**
     * 用 live binding 重写倒角命令。
     *
     * @param command 原始倒角命令。
     * @returns 重写结果。
     */
    private rewriteChamferCommandWithLiveBindings(command: CadCommand): RewriteCommandResult {
        const bindings = this.dependencyGraph.getBindingsByFeatureId(command.id);
        if (bindings.length <= 0) {
            return {
                command: structuredClone(command),
                changed: false,
            };
        }

        const nextCommand = structuredClone(command);
        const nextParams = {...(nextCommand.params as Record<string, unknown>)} as ChamferParams & Record<string, unknown>;
        const liveResolution = this.resolveLiveModelTopologyBindings(bindings);
        if (!liveResolution) {
            nextCommand.params = nextParams;
            return {
                command: nextCommand,
                changed: false,
            };
        }

        const nextDefinition = this.rewriteChamferDefinitionWithLiveBindings(
            nextParams.definition,
            liveResolution.itemMap,
            liveResolution.pointMap,
        );
        const changed = !this.areJsonLikeValuesEqual(nextParams.definition, nextDefinition)
            || !this.areJsonLikeValuesEqual(nextParams.referenceBindings, liveResolution.serializedBindings);

        nextParams.definition = nextDefinition;
        nextParams.referenceBindings = liveResolution.serializedBindings;
        nextCommand.params = nextParams;
        return {
            command: nextCommand,
            changed,
        };
    }

    /**
     * 解析当前命令对应的 live 模型拓扑绑定。
     *
     * @param bindings 命令绑定集合。
     * @returns 绑定解析结果；不存在可用模型拓扑绑定时返回 `null`。
     */
    private resolveLiveModelTopologyBindings(bindings: Array<{ serialize: () => SerializedFeatureReferenceBinding; resolve: (context: { viewer: Viewer }) => unknown }>): {
        itemMap: Map<string, SelectionItemRef>;
        pointMap: Map<string, BlendPointRef>;
        serializedBindings: SerializedFeatureReferenceBinding[];
    } | null {
        const itemMap = new Map<string, SelectionItemRef>();
        const pointMap = new Map<string, BlendPointRef>();
        let hasModelTopologyBinding = false;

        const serializedBindings = bindings.map((binding) => {
            const serializedBinding = binding.serialize();
            if (serializedBinding.kind !== 'model-topology') {
                return serializedBinding;
            }

            hasModelTopologyBinding = true;
            const resolved = binding.resolve({
                viewer: this.viewer,
            });
            if (!resolved || (resolved as { kind?: unknown }).kind !== 'model-topology') {
                return serializedBinding;
            }

            const resolvedModelBinding = resolved as {
                items: SelectionItemRef[];
                pointRefs: BlendPointRef[];
            };
            const originalItems = serializedBinding.items ?? [];
            const originalPointRefs = serializedBinding.pointRefs ?? [];

            originalItems.forEach((item, index) => {
                itemMap.set(
                    this.buildSelectionIdentityKey(item),
                    structuredClone(resolvedModelBinding.items[index] ?? item),
                );
            });
            originalPointRefs.forEach((pointRef, index) => {
                pointMap.set(
                    this.buildPointIdentityKey(pointRef),
                    structuredClone(resolvedModelBinding.pointRefs[index] ?? pointRef),
                );
            });

            return ModelTopologyFeatureBinding.createPayload({
                bodyObjectId: serializedBinding.bodyObjectId,
                items: resolvedModelBinding.items,
                pointRefs: resolvedModelBinding.pointRefs,
            });
        });

        if (!hasModelTopologyBinding) {
            return null;
        }

        return {
            itemMap,
            pointMap,
            serializedBindings,
        };
    }

    /**
     * 构建选择项稳定键。
     *
     * @param item 选择项。
     * @returns 稳定键。
     */
    private buildSelectionIdentityKey(item: SelectionItemRef): string {
        const identity = item.subEntityId
            ?? item.runtimeTopologyHint?.subEntityId
            ?? item.persistentRef
            ?? item.runtimeTopologyHint?.persistentRef
            ?? 'unknown';
        return `${item.kind}:${item.bodyObjectId}:${identity}`;
    }

    /**
     * 构建点引用稳定键。
     *
     * @param pointRef 点引用。
     * @returns 稳定键。
     */
    private buildPointIdentityKey(pointRef: BlendPointRef): string {
        const identity = pointRef.subEntityId
            ?? pointRef.runtimeTopologyHint?.subEntityId
            ?? pointRef.persistentRef
            ?? pointRef.runtimeTopologyHint?.persistentRef
            ?? pointRef.point.join(',');
        return `${pointRef.bodyObjectId}:${identity}`;
    }

    /**
     * 用 live 绑定重写单个选择项。
     *
     * @param item 原始选择项。
     * @param itemMap live 选择项映射。
     * @returns 重写后的选择项。
     */
    private rewriteSelectionItemWithLiveBinding(
        item: SelectionItemRef,
        itemMap: Map<string, SelectionItemRef>,
    ): SelectionItemRef {
        return structuredClone(itemMap.get(this.buildSelectionIdentityKey(item)) ?? item);
    }

    /**
     * 用 live 绑定重写单个点引用。
     *
     * @param pointRef 原始点引用。
     * @param pointMap live 点引用映射。
     * @returns 重写后的点引用。
     */
    private rewritePointRefWithLiveBinding(
        pointRef: BlendPointRef | null | undefined,
        pointMap: Map<string, BlendPointRef>,
    ): BlendPointRef | null {
        if (!pointRef) {
            return null;
        }
        return structuredClone(pointMap.get(this.buildPointIdentityKey(pointRef)) ?? pointRef);
    }

    /**
     * 用 live 绑定重写部分边线参数。
     *
     * 说明：
     * 1. 部分边线自身仍然绑定到主选择边，因此 itemRef 走拓扑 ref remap；
     * 2. 参考等距现在只允许点引用，因此参考端点走 point binding；
     * 3. 返回对象只写入当前协议字段，避免旧的 reference item/ref 字段继续进入命令链路。
     *
     * @param partialEdge 原始部分边线参数。
     * @param refRemap 主选择项 ref 重映射。
     * @param pointMap live 点引用映射。
     * @returns 重写后的部分边线参数。
     */
    private rewritePartialEdgeParamWithLiveBindings(
        partialEdge: PartialEdgeParam,
        refRemap: Map<string, string>,
        pointMap: Map<string, BlendPointRef>,
    ): PartialEdgeParam {
        return {
            itemRef: this.rewritePersistentRefWithRemap(partialEdge.itemRef, refRemap) ?? partialEdge.itemRef,
            startCondition: partialEdge.startCondition,
            startValue: partialEdge.startValue,
            startExpression: partialEdge.startExpression,
            startReferencePoint: this.rewritePointRefWithLiveBinding(partialEdge.startReferencePoint ?? null, pointMap),
            endCondition: partialEdge.endCondition,
            endValue: partialEdge.endValue,
            endExpression: partialEdge.endExpression,
            endReferencePoint: this.rewritePointRefWithLiveBinding(partialEdge.endReferencePoint ?? null, pointMap),
        };
    }

    /**
     * 根据原始选择项与 live 选择项生成持久化引用重映射。
     *
     * @param originalItems 原始选择项。
     * @param rewrittenItems 已重写的选择项。
     * @returns 旧 ref 到新 ref 的映射。
     */
    private buildPersistentRefRemap(
        originalItems: SelectionItemRef[],
        rewrittenItems: SelectionItemRef[],
    ): Map<string, string> {
        return originalItems.reduce<Map<string, string>>((remap, originalItem, index) => {
            const rewrittenItem = rewrittenItems[index];
            if (
                rewrittenItem
                && typeof originalItem.persistentRef === 'string'
                && originalItem.persistentRef.length > 0
                && typeof rewrittenItem.persistentRef === 'string'
                && rewrittenItem.persistentRef.length > 0
            ) {
                remap.set(originalItem.persistentRef, rewrittenItem.persistentRef);
            }
            return remap;
        }, new Map<string, string>());
    }

    /**
     * 用 ref 重映射重写字符串引用。
     *
     * @param persistentRef 原始字符串引用。
     * @param refRemap 旧 ref 到新 ref 的映射。
     * @returns 重写后的引用。
     */
    private rewritePersistentRefWithRemap(
        persistentRef: string | null | undefined,
        refRemap: Map<string, string>,
    ): string | null | undefined {
        if (typeof persistentRef !== 'string' || persistentRef.length <= 0) {
            return persistentRef;
        }
        return refRemap.get(persistentRef) ?? persistentRef;
    }

    /**
     * 用 live 绑定重写圆角定义。
     *
     * @param definition 原始圆角定义。
     * @param itemMap live 选择项映射。
     * @param pointMap live 点引用映射。
     * @returns 重写后的圆角定义。
     */
    private rewriteFilletDefinitionWithLiveBindings(
        definition: FilletFeatureDefinition,
        itemMap: Map<string, SelectionItemRef>,
        pointMap: Map<string, BlendPointRef>,
    ): FilletFeatureDefinition {
        const rewrittenItems = (definition.items ?? []).map((item) => this.rewriteSelectionItemWithLiveBinding(item, itemMap));
        const rewrittenBoundaryItems = (definition.boundaryItems ?? []).map((item) => this.rewriteSelectionItemWithLiveBinding(item, itemMap));
        const rewrittenFaceSets = (definition.faceSets ?? []).map((faceSet) => ({
            ...structuredClone(faceSet),
            items: (faceSet.items ?? []).map((item) => this.rewriteSelectionItemWithLiveBinding(item, itemMap)),
        }));
        const allOriginalItems = [
            ...(definition.items ?? []),
            ...(definition.boundaryItems ?? []),
            ...(definition.faceSets ?? []).flatMap((faceSet) => faceSet.items ?? []),
        ];
        const allRewrittenItems = [
            ...rewrittenItems,
            ...rewrittenBoundaryItems,
            ...rewrittenFaceSets.flatMap((faceSet) => faceSet.items ?? []),
        ];
        const refRemap = this.buildPersistentRefRemap(allOriginalItems, allRewrittenItems);

        return {
            ...structuredClone(definition),
            items: rewrittenItems,
            boundaryItems: rewrittenBoundaryItems,
            boundaryRefs: (definition.boundaryRefs ?? []).map((persistentRef) => this.rewritePersistentRefWithRemap(persistentRef, refRemap) ?? persistentRef),
            faceSets: rewrittenFaceSets,
            partialEdges: (definition.partialEdges ?? []).map((partialEdge) => this.rewritePartialEdgeParamWithLiveBindings(
                partialEdge,
                refRemap,
                pointMap,
            )),
            variablePoints: (definition.variablePoints ?? []).map((point) => {
                const nextItemRef = this.rewritePersistentRefWithRemap(point.itemRef, refRemap) ?? point.itemRef;
                const label = point.label ?? point.key.slice(point.key.lastIndexOf(':') + 1);
                return {
                    ...structuredClone(point),
                    itemRef: nextItemRef,
                    label,
                    key: `${nextItemRef}:${label}`,
                };
            }),
            helpPoint: this.rewritePointRefWithLiveBinding(definition.helpPoint ?? null, pointMap),
        };
    }

    /**
     * 用 live 绑定重写倒角定义。
     *
     * @param definition 原始倒角定义。
     * @param itemMap live 选择项映射。
     * @param pointMap live 点引用映射。
     * @returns 重写后的倒角定义。
     */
    private rewriteChamferDefinitionWithLiveBindings(
        definition: ChamferFeatureDefinition,
        itemMap: Map<string, SelectionItemRef>,
        pointMap: Map<string, BlendPointRef>,
    ): ChamferFeatureDefinition {
        const rewrittenItems = (definition.items ?? []).map((item) => this.rewriteSelectionItemWithLiveBinding(item, itemMap));
        const rewrittenFaceSets = (definition.faceSets ?? []).map((faceSet) => ({
            ...structuredClone(faceSet),
            items: (faceSet.items ?? []).map((item) => this.rewriteSelectionItemWithLiveBinding(item, itemMap)),
        }));
        const refRemap = this.buildPersistentRefRemap(
            [
                ...(definition.items ?? []),
                ...(definition.faceSets ?? []).flatMap((faceSet) => faceSet.items ?? []),
            ],
            [
                ...rewrittenItems,
                ...rewrittenFaceSets.flatMap((faceSet) => faceSet.items ?? []),
            ],
        );

        return {
            ...structuredClone(definition),
            items: rewrittenItems,
            faceSets: rewrittenFaceSets,
            partialEdges: (definition.partialEdges ?? []).map((partialEdge) => this.rewritePartialEdgeParamWithLiveBindings(
                partialEdge,
                refRemap,
                pointMap,
            )),
            helpPoint: this.rewritePointRefWithLiveBinding(definition.helpPoint ?? null, pointMap),
        };
    }

    /**
     * 比较 JSON-like 值是否相等。
     * 说明：
     * 1. 这里只用于比较命令参数中的轮廓、平面和草图快照；
     * 2. 输入值均来自结构化序列化数据，不包含函数和循环引用；
     * 3. 该比较只作用于重建前的参数变化判定，不作为通用对象比较工具向外暴露。
     * @param left 左值。
     * @param right 右值。
     * @returns 是否相等。
     */
    private areJsonLikeValuesEqual(left: unknown, right: unknown): boolean {
        if (left === right) {
            return true;
        }
        if (Array.isArray(left) && Array.isArray(right)) {
            if (left.length !== right.length) {
                return false;
            }
            return left.every((item, index) => this.areJsonLikeValuesEqual(item, right[index]));
        }
        if (
            typeof left === 'object'
            && left !== null
            && typeof right === 'object'
            && right !== null
        ) {
            const leftEntries = Object.entries(left as Record<string, unknown>).sort(([leftKey], [rightKey]) => leftKey.localeCompare(rightKey));
            const rightEntries = Object.entries(right as Record<string, unknown>).sort(([leftKey], [rightKey]) => leftKey.localeCompare(rightKey));
            if (leftEntries.length !== rightEntries.length) {
                return false;
            }
            return leftEntries.every(([leftKey, leftValue], index) => {
                const [rightKey, rightValue] = rightEntries[index] ?? [];
                return leftKey === rightKey && this.areJsonLikeValuesEqual(leftValue, rightValue);
            });
        }
        return false;
    }

}
